import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { listMySpots } from '../api/spots';
import type { SpotMineResponse } from '../types/api';
import { TabsRow } from '../components/spots/TabsRow';
import { SpotDashboard } from '../components/spots/SpotDashboard';

export function SpotsPage() {
  const navigate = useNavigate();
  const { spotId } = useParams<{ spotId?: string }>();

  const [spots, setSpots] = useState<SpotMineResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listMySpots()
      .then(setSpots)
      .catch(() => setError('Failed to load spots.'))
      .finally(() => setLoading(false));
  }, []);

  const parsedId = spotId ? parseInt(spotId, 10) : null;
  const activeSpot = spots.find((s) => s.id === parsedId) ?? spots[0] ?? null;

  useEffect(() => {
    if (!loading && spots.length > 0 && !parsedId) {
      navigate(`/spots/${spots[0].id}`, { replace: true });
    }
  }, [loading, spots, parsedId, navigate]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-9 w-24 animate-pulse rounded-full bg-slate-100 dark:bg-slate-800" />
          ))}
        </div>
        <div className="h-64 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" />
      </div>
    );
  }

  if (error) return <p className="p-6 text-red-500">{error}</p>;

  if (spots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 p-6">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-16 h-16 text-slate-300 dark:text-slate-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
        </svg>
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-700 dark:text-slate-300">No spots available yet</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Spots will appear here once they've been added. Check back soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50 mb-4">Spots</h1>
        <TabsRow
          spots={spots}
          activeId={activeSpot?.id ?? -1}
          onSelect={(id) => navigate(`/spots/${id}`, { replace: true })}
        />
      </div>
      {activeSpot && <SpotDashboard spot={activeSpot} />}
    </div>
  );
}
